import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styles from './styles.module.scss';
import { CaseType, ICase } from '@brands/services/cases/types/ICase';
import useCases from '@brands/hooks/useCases';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { selectAuth } from '@brands/store/selectors/auth';
import EditCreditCardModal from '../../Appointments/Patient/EditCreditCardModal/EditCreditCardModal';
import UpcomingServices from '../../Dashboard/Patient/components/UpcomingServices';

const UpcomingAppointments = (): JSX.Element => {
  const [displayingCases, setDisplayingCases] = useState<ICase[]>([]);
  const [selectedCaseFromUpcomingServices, setSelectedCaseFromUpcomingServices] = useState<ICase>();
  const [editCreditCardModal, setEditCreditCardModal] = useState(false);
  const observer = useRef<IntersectionObserver>();
  const lastMyCasesItemRef = useRef<HTMLTableRowElement>(null);
  const { userInfo } = useAppSelector(selectAuth);
  const [isLoading, setIsLoading] = useState(false);
  const params = useMemo(
    () => ({
      sort_by: 'created_at' as const,
      sort_direction: 'desc' as const,
      patient_id: Number(userInfo.id),
      include_vc_stats: true,
      type: [
        CaseType.one_report,
        CaseType.second_opinion,
        CaseType.video_call_scheduled,
        CaseType.phone_call_scheduled,
        CaseType.care_coordination,
        CaseType.oral_health_coaching,
      ],
      status: [
        'new',
        'accepted_care_advisor',
        'accepted_provider',
        'in_progress_provider',
        'in_progress_care_advisor',
        'completed_by_provider',
      ],
      include_dependents_cases: true,
    }),
    [userInfo.id],
  );

  const { cases, dataLoading, reloadAll, loadNextPage } = useCases(params, true);

  useEffect(() => {
    if (cases) {
      setDisplayingCases(cases);
    }
  }, [cases]);

  const loadMore = (): void => {
    setIsLoading(true);
    loadNextPage().then(() => {
      setIsLoading(false);
    });
  };

  const handleIntersection = (entries: IntersectionObserverEntry[]): void => {
    const lastItem = entries[0];
    if (lastItem.isIntersecting && !isLoading) {
      loadMore();
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(handleIntersection);

      const currentRef = lastMyCasesItemRef.current;
      if (currentRef) {
        observer.current.observe(currentRef);
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [cases]);
  
  return (
    <div className={styles.dashboardContainer}>
      {displayingCases.length > 0 && (
        <UpcomingServices
          displayingCases={displayingCases}
          dataLoading={dataLoading}
          reloadAll={reloadAll}
          setEditCreditCardModal={setEditCreditCardModal}
          selectedCaseFromUpcomingServices={selectedCaseFromUpcomingServices}
          setSelectedCaseFromUpcomingServices={setSelectedCaseFromUpcomingServices}
          lastMyCasesItemRef={lastMyCasesItemRef}
          isLoading={isLoading}
        />
      )}
      {editCreditCardModal && (
          <EditCreditCardModal
            setOpenModal={setEditCreditCardModal}
            isOpen={editCreditCardModal}
            thisCase={selectedCaseFromUpcomingServices!}
            reloadAll={reloadAll}
            refreshAll={reloadAll}
          />
        )}
    </div>
  );
};

export default UpcomingAppointments;
